import React, { useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  ButtonGroup,
  Card,
  Form,
  Modal,
  Spinner,
  Table,
} from "react-bootstrap";
import { Toaster } from "react-hot-toast";
import { notifyError, notifySuccess } from "../../../components/AppToast";
import dayjs from "dayjs";
import { formatTime12h } from "../../../utils/formatTime";
import { ClassesCalendar } from "../../../components/ClassesCalendar";
import "./ClasesAdminPage.css";
import { CreateClassModal } from "../../teacher/components/CreateClassModal";
import { useTeachers } from "../../teacher/hooks/usePlannings";
import { useSedes } from "../../teacher/hooks/useSedes";
import { useClassrooms } from "../hooks/useClassrooms";
import {
  useAllPlannings,
  useAdminUpdatePlanning,
  useAdminReschedulePlanning,
  useAdminDeletePlanning,
} from "../hooks/useAllPlannings";

// Estado del backend: "active" = fecha futura, "draft" = ya pasó, "cancelled".
const STATUS = {
  active: { label: "Programada", bg: "success" },
  cancelled: { label: "Cancelada", bg: "danger" },
  draft: { label: "Finalizada", bg: "secondary" },
};

const UNASSIGNED = "__unassigned__";

// Paleta para colorear el calendario global por la dimensión activa (docente o aula).
const RESOURCE_PALETTE = [
  "#1f4e79", "#c1121f", "#1f9d6b", "#e0a100", "#6f42c1",
  "#0d9488", "#d6336c", "#2563a8", "#b45309", "#4b5563",
];
const UNASSIGNED_COLOR = "#adb5bd";

// Dimensiones por las que el propietario puede agrupar el calendario (O1 / O3).
const GROUPINGS = [
  { key: "instructor", label: "Docente", unassigned: "Sin asignar" },
  { key: "classroom", label: "Aula", unassigned: "Sin aula" },
];

// Una clase finalizada (ya inició/pasó o tiene calificaciones) no se puede mover.
const isFinalized = (p) => p?.finalized === true || p?.status !== "active";

function classTitle(p) {
  const topics = (p.topics ?? []).map((t) => t.name ?? t).filter(Boolean);
  if (topics.length > 0) return topics.join(", ");
  return p.title && p.title !== "Clase programada" ? p.title : "Clase";
}

function InstructorCell({ instructor }) {
  if (!instructor) {
    return (
      <Badge bg="warning" text="dark">
        Sin asignar
      </Badge>
    );
  }
  return <span>{instructor.name}</span>;
}

function ClasesAdminPage() {
  const { data: plannings = [], isLoading, isError } = useAllPlannings();
  const { data: teachers = [] } = useTeachers();
  const { data: sedes = [] } = useSedes();
  const { data: aulas = [] } = useClassrooms();
  const updateMutation = useAdminUpdatePlanning();
  const rescheduleMutation = useAdminReschedulePlanning();
  const deleteMutation = useAdminDeletePlanning();

  const [query, setQuery] = useState("");
  const [instructorFilter, setInstructorFilter] = useState("");
  const [sedeFilter, setSedeFilter] = useState("");
  const [groupBy, setGroupBy] = useState("instructor"); // instructor | classroom
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // clase a eliminar
  const [view, setView] = useState("calendar"); // calendar | table

  const byClassroom = groupBy === "classroom";
  const grouping = GROUPINGS.find((g) => g.key === groupBy) ?? GROUPINGS[0];

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return plannings.filter((p) => {
      if (sedeFilter && String(p.headquarters_id ?? "") !== sedeFilter) return false;
      if (instructorFilter === UNASSIGNED && p.instructor) return false;
      if (
        instructorFilter &&
        instructorFilter !== UNASSIGNED &&
        String(p.instructor?.id ?? "") !== instructorFilter
      )
        return false;
      if (!q) return true;
      const hay = [
        classTitle(p),
        p.instructor?.name ?? "",
        p.place ?? "",
        p.classroom?.name ?? "",
      ]
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [plannings, query, instructorFilter, sedeFilter]);

  // Recursos de la dimensión activa (columnas del calendario día/semana):
  // docentes, o aulas (activas, acotadas por la sede filtrada).
  const dimResources = useMemo(() => {
    if (byClassroom) {
      return aulas
        .filter(
          (a) =>
            a.is_active !== false &&
            (!sedeFilter || String(a.headquarters ?? "") === sedeFilter)
        )
        .map((a) => ({ id: a.id, title: a.name }));
    }
    return teachers.map((t) => ({ id: t.id, title: t.name }));
  }, [byClassroom, aulas, teachers, sedeFilter]);

  const resources = useMemo(
    () => [{ id: UNASSIGNED, title: grouping.unassigned }, ...dimResources],
    [dimResources, grouping.unassigned]
  );

  // Color estable por recurso de la dimensión activa.
  const colorByResource = useMemo(() => {
    const m = new Map();
    dimResources.forEach((r, i) =>
      m.set(r.id, RESOURCE_PALETTE[i % RESOURCE_PALETTE.length])
    );
    return m;
  }, [dimResources]);

  const events = useMemo(
    () =>
      filtered.map((p) => {
        const start = dayjs(`${p.date}T${p.time_start || "00:00"}`).toDate();
        const end = dayjs(start).add(p.duration || 1, "hour").toDate();
        return {
          id: p.id,
          title: classTitle(p),
          start,
          end,
          resource: p,
          // Columna del calendario según la dimensión: docente o aula de la clase.
          resourceId: byClassroom
            ? p.classroom?.id ?? UNASSIGNED
            : p.instructor?.id ?? UNASSIGNED,
        };
      }),
    [filtered, byClassroom]
  );

  // IDs de los recursos en posición impar → su bloque semanal se tinta (alterna).
  // react-big-calendar pasa a los getters el ID del recurso (no el objeto), así
  // que comparamos contra este conjunto de IDs.
  const tintedResourceIds = useMemo(() => {
    const set = new Set();
    resources.forEach((r, i) => {
      if (i % 2 === 1) set.add(r.id);
    });
    return set;
  }, [resources]);

  // Color del evento: por el recurso de la dimensión activa; gris si ya finalizó.
  const eventPropGetter = (event) => {
    const p = event.resource;
    const rid = byClassroom ? p?.classroom?.id : p?.instructor?.id;
    const bg = isFinalized(p) ? UNASSIGNED_COLOR : colorByResource.get(rid) ?? "#6c757d";
    return { style: { backgroundColor: bg, borderColor: bg, color: "#fff" } };
  };

  // Tinte de fondo alterno por docente (vista Semana/Día): el bloque Lun→Dom de un
  // docente va en azul suave y el del siguiente en blanco. Se pinta cada celda de
  // forma OPACA (azul o blanco): el blanco tapa el resaltado nativo de "hoy" (que
  // si no, deja el domingo marcado y rompe la uniformidad del bloque). El 2º
  // argumento que entrega react-big-calendar es el ID del recurso, no el objeto.
  const slotPropGetter = (_date, resourceId) => ({
    style: {
      backgroundColor: tintedResourceIds.has(resourceId) ? "#e5eefb" : "#ffffff",
    },
  });

  // O2/O3 + reprogramar: soltar un evento cambia fecha/hora (y duración al
  // redimensionar); si se suelta en la columna de otro recurso, reasigna el
  // docente (agrupado por docente) o el aula (agrupado por aula). El backend
  // valida conflictos (409) y notifica a los inscritos.
  const handleDrop = ({ event, start, end, resourceId }) => {
    const p = event.resource;
    if (isFinalized(p)) {
      notifyError("No puedes mover una clase finalizada.");
      return;
    }
    const s = dayjs(start);
    const duration = Math.max(1, Math.round(dayjs(end).diff(s, "minute") / 60));
    const patch = {
      date: s.format("YYYY-MM-DD"),
      time_start: s.format("HH:mm"),
      duration,
    };
    const optimistic = { ...patch };

    // Reasignar si se soltó en la columna de otro recurso (solo día/semana).
    let reassignLabel = null;
    if (resourceId !== undefined) {
      const targetId = resourceId === UNASSIGNED ? null : resourceId;
      if (byClassroom) {
        const currentId = p.classroom?.id ?? null;
        if (targetId !== currentId) {
          patch.classroom = targetId;
          const a = aulas.find((x) => x.id === targetId);
          // El aula pertenece a una sede: si es distinta, mover también la sede.
          if (a?.headquarters && String(a.headquarters) !== String(p.headquarters_id)) {
            patch.headquarters = a.headquarters;
          }
          optimistic.classroom = targetId
            ? { id: targetId, name: a?.name ?? "Aula", headquarters_id: a?.headquarters }
            : null;
          reassignLabel = "Aula reasignada";
        }
      } else {
        const currentId = p.instructor?.id ?? null;
        if (targetId !== currentId) {
          patch.instructor = targetId;
          const t = teachers.find((x) => x.id === targetId);
          optimistic.instructor = targetId
            ? { id: targetId, name: t?.name ?? "Docente" }
            : null;
          reassignLabel = "Instructor reasignado";
        }
      }
    }

    rescheduleMutation.mutate(
      { id: event.id, patch, optimistic },
      {
        onSuccess: (data) => {
          const n = data?._notified ?? 0;
          const base = reassignLabel ?? "Clase reprogramada";
          notifySuccess(
            n > 0 ? `${base}. Se notificó a ${n} estudiante(s).` : `${base}.`
          );
        },
        onError: (err) =>
          notifyError(
            err?.response?.data?.detail ?? "No se pudo actualizar la clase."
          ),
      }
    );
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    deleteMutation.mutate(deleteTarget.id, {
      onSuccess: (res) => {
        const n = res?._notified ?? 0;
        notifySuccess(
          n > 0
            ? `Clase eliminada. Se notificó a ${n} estudiante(s).`
            : "Clase eliminada."
        );
        setDeleteTarget(null);
      },
      onError: (err) =>
        notifyError(
          err?.response?.data?.detail ?? "No se pudo eliminar la clase.",
          "No se pudo eliminar"
        ),
    });
  };

  const handleSave = (data) => {
    if (!editing) return;
    updateMutation.mutate(
      { id: editing.id, data },
      {
        onSuccess: (res) => {
          const n = res?._notified ?? 0;
          notifySuccess(
            n > 0
              ? `Clase actualizada — ${n} estudiante(s) notificado(s).`
              : "Clase actualizada."
          );
          setEditing(null);
        },
        onError: (err) =>
          notifyError(
            err?.response?.data?.detail ?? "No se pudo actualizar la clase."
          ),
      }
    );
  };

  return (
    <div>
      <Toaster position="top-right" />

      <div className="mb-3">
        <h4 className="mb-1">Clases de la academia</h4>
        <p className="text-muted small mb-0">
          Vista global de las clases de todos los docentes. Puedes reasignar el
          instructor, mover la fecha u hora y ajustar otros datos de las clases
          que aún no han iniciado.
        </p>
      </div>

      <div className="d-flex flex-wrap gap-2 mb-3 align-items-center">
        <Form.Control
          placeholder="Buscar por tema, docente o sede…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ maxWidth: 320 }}
        />
        <Form.Select
          value={instructorFilter}
          onChange={(e) => setInstructorFilter(e.target.value)}
          style={{ maxWidth: 240 }}
          aria-label="Filtrar por docente"
        >
          <option value="">Todos los docentes</option>
          {teachers.map((t) => (
            <option key={t.id} value={String(t.id)}>
              {t.name}
            </option>
          ))}
          <option value={UNASSIGNED}>Sin asignar</option>
        </Form.Select>
        {sedes.length > 1 && (
          <Form.Select
            value={sedeFilter}
            onChange={(e) => setSedeFilter(e.target.value)}
            style={{ maxWidth: 220 }}
            aria-label="Filtrar por sede"
          >
            <option value="">Todas las sedes</option>
            {sedes.map((s) => (
              <option key={s.id} value={String(s.id)}>
                {s.reference_name || s.address || `Sede ${s.id}`}
              </option>
            ))}
          </Form.Select>
        )}
        <ButtonGroup size="sm" className="ms-auto" aria-label="Cambiar vista">
          <Button
            variant={view === "calendar" ? "primary" : "outline-primary"}
            onClick={() => setView("calendar")}
          >
            <i className="mdi mdi-calendar-month-outline me-1" aria-hidden="true" />
            Calendario
          </Button>
          <Button
            variant={view === "table" ? "primary" : "outline-primary"}
            onClick={() => setView("table")}
          >
            <i className="mdi mdi-table me-1" aria-hidden="true" />
            Tabla
          </Button>
        </ButtonGroup>
      </div>

      {view === "calendar" && !isLoading && !isError && (
        <Card className="border-0 shadow-sm mb-3">
          <Card.Body>
            {/* Agrupar por: docente (O1) o aula (O3) */}
            <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
              <span className="text-muted small">Agrupar por:</span>
              <ButtonGroup size="sm" aria-label="Agrupar el calendario por">
                {GROUPINGS.map((g) => (
                  <Button
                    key={g.key}
                    variant={groupBy === g.key ? "primary" : "outline-primary"}
                    onClick={() => setGroupBy(g.key)}
                  >
                    {g.label}
                  </Button>
                ))}
              </ButtonGroup>
              <span className="text-muted small ms-1 d-none d-md-inline">
                (columnas por {grouping.label.toLowerCase()} en vista Semana/Día)
              </span>
            </div>
            {/* Leyenda: color por la dimensión activa */}
            <div className="d-flex flex-wrap gap-3 mb-3 small">
              {dimResources.map((r) => (
                <span key={r.id} className="d-inline-flex align-items-center gap-1">
                  <span
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: 3,
                      background: colorByResource.get(r.id),
                      display: "inline-block",
                    }}
                  />
                  {r.title}
                </span>
              ))}
              <span className="d-inline-flex align-items-center gap-1 text-muted">
                <span
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: 3,
                    background: UNASSIGNED_COLOR,
                    display: "inline-block",
                  }}
                />
                {grouping.unassigned} / finalizada
              </span>
            </div>
            <div className="owner-week-calendar">
              <ClassesCalendar
                events={events}
                defaultView="month"
                onSelectEvent={(e) => {
                  if (isFinalized(e.resource)) {
                    notifyError(
                      "No puedes editar una clase finalizada (ya inició o tiene calificaciones)."
                    );
                    return;
                  }
                  setEditing(e.resource);
                }}
                eventPropGetter={eventPropGetter}
                slotPropGetter={slotPropGetter}
                onEventDrop={handleDrop}
                onEventResize={handleDrop}
                draggableAccessor={(e) => !isFinalized(e.resource)}
                resources={resources}
                resourceIdAccessor="id"
                resourceTitleAccessor="title"
              />
            </div>
            <p className="text-muted small mb-0 mt-2">
              Color por {grouping.label.toLowerCase()} ·{" "}
              <strong>arrastra</strong> para reprogramar · en vista{" "}
              <strong>Semana/Día</strong> cada {grouping.label.toLowerCase()} es
              una columna: arrastra una clase a otra columna para{" "}
              <strong>
                reasignar {byClassroom ? "el aula" : "el instructor"}
              </strong>
              . Clic en una clase para editarla.
            </p>
          </Card.Body>
        </Card>
      )}

      {isLoading && (
        <div className="text-center py-4">
          <Spinner animation="border" size="sm" />
        </div>
      )}
      {isError && (
        <Alert variant="danger">No se pudieron cargar las clases.</Alert>
      )}
      {view === "table" && !isLoading && !isError && filtered.length === 0 && (
        <Card>
          <p className="text-muted text-center py-4 mb-0">
            No hay clases que coincidan.
          </p>
        </Card>
      )}

      {view === "table" && filtered.length > 0 && (
        <>
          {/* PC: tabla */}
          <Card className="d-none d-md-block border-0 shadow-sm">
            <Card.Body className="p-0">
              <Table responsive hover className="mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Fecha y hora</th>
                    <th>Clase</th>
                    <th>Instructor</th>
                    <th>Sede</th>
                    <th className="text-center">Nivel</th>
                    <th className="text-center">Estado</th>
                    <th className="text-center">Inscritos</th>
                    <th style={{ width: 110 }} />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p) => {
                    const st = STATUS[p.status] ?? STATUS.draft;
                    return (
                      <tr key={p.id}>
                        <td>
                          <div className="fw-medium">
                            {dayjs(p.date).format("ddd D MMM YYYY")}
                          </div>
                          {p.time_start && (
                            <div className="text-muted small">
                              {formatTime12h(p.time_start)}
                              {p.duration ? ` · ${p.duration} h` : ""}
                            </div>
                          )}
                        </td>
                        <td>{classTitle(p)}</td>
                        <td>
                          <InstructorCell instructor={p.instructor} />
                        </td>
                        <td className="text-muted">{p.place ?? "—"}</td>
                        <td className="text-center text-muted">
                          {p.level?.abbreviation ?? p.level?.name ?? "—"}
                        </td>
                        <td className="text-center">
                          <Badge bg={st.bg}>{st.label}</Badge>
                        </td>
                        <td className="text-center">{p.enrolled_count ?? 0}</td>
                        <td className="text-end">
                          <div className="d-inline-flex gap-2">
                            {!p.finalized && (
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => setEditing(p)}
                                title="Editar"
                              >
                                <i className="mdi mdi-pencil-outline" />
                              </Button>
                            )}
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => setDeleteTarget(p)}
                              title="Eliminar clase"
                            >
                              <i className="mdi mdi-trash-can-outline" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </Card.Body>
          </Card>

          {/* Móvil: tarjetas */}
          <div className="d-md-none d-flex flex-column gap-2">
            {filtered.map((p) => {
              const st = STATUS[p.status] ?? STATUS.draft;
              return (
                <Card key={p.id} className="border-0 shadow-sm">
                  <Card.Body className="py-2 px-3">
                    <div className="d-flex justify-content-between align-items-start gap-2 mb-1">
                      <div style={{ minWidth: 0 }}>
                        <div className="fw-medium">{classTitle(p)}</div>
                        <div className="text-muted small">
                          {dayjs(p.date).format("ddd D MMM YYYY")}
                          {p.time_start ? ` · ${formatTime12h(p.time_start)}` : ""}
                        </div>
                      </div>
                      <Badge bg={st.bg} className="flex-shrink-0">
                        {st.label}
                      </Badge>
                    </div>
                    <div className="small mb-2">
                      <span className="text-muted">Instructor: </span>
                      <InstructorCell instructor={p.instructor} />
                      {p.place && (
                        <span className="text-muted"> · {p.place}</span>
                      )}
                      <span className="text-muted">
                        {" "}
                        · {p.enrolled_count ?? 0} inscrito(s)
                      </span>
                      {p.level && (
                        <span className="text-muted">
                          {" "}
                          · Nivel: {p.level.abbreviation ?? p.level.name}
                        </span>
                      )}
                    </div>
                    <div className="d-flex gap-2">
                      {!p.finalized && (
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => setEditing(p)}
                        >
                          <i className="mdi mdi-pencil-outline me-1" />
                          Editar
                        </Button>
                      )}
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => setDeleteTarget(p)}
                      >
                        <i className="mdi mdi-trash-can-outline me-1" />
                        Eliminar
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              );
            })}
          </div>
        </>
      )}

      <CreateClassModal
        show={editing !== null}
        onHide={() => setEditing(null)}
        onCreate={handleSave}
        isLoading={updateMutation.isPending}
        error={updateMutation.error}
        mode="edit"
        initial={editing}
        onDelete={() => {
          const target = editing;
          setEditing(null);
          setDeleteTarget(target);
        }}
      />

      {/* Confirmación de borrado (propietario) */}
      <Modal
        show={deleteTarget !== null}
        onHide={() => setDeleteTarget(null)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Eliminar clase</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          ¿Seguro que quieres eliminar la clase{" "}
          <strong>«{deleteTarget ? classTitle(deleteTarget) : ""}»</strong>
          {deleteTarget?.date
            ? ` del ${dayjs(deleteTarget.date).format("D MMM YYYY")}`
            : ""}
          ?
          {(deleteTarget?.enrolled_count ?? 0) > 0 && (
            <div className="text-warning small mt-2">
              <i className="mdi mdi-alert-outline me-1" aria-hidden="true" />
              Tiene {deleteTarget.enrolled_count} estudiante(s) inscrito(s): se
              les notificará y se retirará su reserva.
            </div>
          )}
          <div className="text-muted small mt-2">
            Esta acción no se puede deshacer. Las clases con calificaciones no se
            pueden eliminar.
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setDeleteTarget(null)}>
            Cancelar
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
            style={{ minHeight: 44 }}
          >
            {deleteMutation.isPending ? (
              <Spinner size="sm" animation="border" />
            ) : (
              "Eliminar"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export { ClasesAdminPage };
